// The ☰ Filters button, the panel behind it, and the chips beside it: one shell for both tabs'
// filter bars (My Accounts and All accounts, GRO-600). Each tab passes its own panel contents and
// its own chips; the button, the badge, the panel's placement and how it opens and closes, the
// Clear all footer and the chip style are this file's, so the two bars look and behave alike.
//
// The one risk a collapsed panel introduces is a filter you forgot you set, which is
// indistinguishable from missing data. That is what the chips are for: **whatever is active
// stays on the page**, named and individually removable, whether the panel is open or shut.
// The button also carries a count, so a filtered board never looks like an unfiltered one.

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { filterPanelPlacement } from './filterPlacement';
import type { FilterPanelPlacement } from './filterPlacement';

export function FilterChip({
  label,
  value,
  onRemove,
  removeLabel,
}: {
  label: string;
  value: string;
  onRemove: () => void;
  removeLabel: string;
}) {
  return (
    <button type="button" className="fchip" aria-label={removeLabel} onClick={onRemove}>
      <span className="fchiplab">{label}</span>
      {value}
      <span className="fchipx" aria-hidden="true">×</span>
    </button>
  );
}

export function FiltersDropdown({
  activeCount,
  isOpen,
  onOpenChange,
  onClearAll,
  note,
  chips,
  children,
}: {
  // How many values behind the button are narrowing the list: the badge.
  activeCount: number;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  // Clears what is behind the button only; the bar's chips and search keep their state.
  onClearAll: () => void;
  // One line in the footer on how the filters combine.
  note: string;
  // The active values, each a removable `FilterChip`.
  chips: ReactNode;
  // The panel's labelled groups (`.fgroup`).
  children: ReactNode;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // The panel is fixed-position, so the table card's overflow cannot clip it. Its place is
  // measured from the button on open and again whenever the window or a parent scrolls.
  const [placement, setPlacement] = useState<FilterPanelPlacement | null>(null);
  useLayoutEffect(() => {
    if (!isOpen) {
      setPlacement(null);
      return;
    }
    function place() {
      const button = buttonRef.current;
      if (!button) return;
      const panelWidth = panelRef.current?.offsetWidth ?? 430;
      setPlacement(filterPanelPlacement(
        button.getBoundingClientRect(), window.innerWidth, window.innerHeight, panelWidth));
    }
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [isOpen]);

  // Straight into the first control on open, so the button and the keyboard agree about what
  // opening the panel was for.
  useEffect(() => {
    if (!isOpen) return;
    panelRef.current?.querySelector('select')?.focus();
  }, [isOpen]);

  // Clicking anywhere else closes the panel; Escape closes it and returns focus to the button,
  // for the same reason the push picker's Cancel does -- the control that opened a thing is
  // where a keyboard user expects to land when it shuts.
  useEffect(() => {
    if (!isOpen) return;

    function onDocumentClick(event: MouseEvent) {
      if (wrapRef.current?.contains(event.target as Node)) return;
      onOpenChange(false);
    }
    function onDocumentKey(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      onOpenChange(false);
      buttonRef.current?.focus();
    }

    document.addEventListener('click', onDocumentClick);
    document.addEventListener('keydown', onDocumentKey);
    return () => {
      document.removeEventListener('click', onDocumentClick);
      document.removeEventListener('keydown', onDocumentKey);
    };
  }, [isOpen, onOpenChange]);

  return (
    <>
      <div className="filterwrap" ref={wrapRef}>
        <button
          type="button"
          ref={buttonRef}
          className={'btn filterbtn' + (activeCount ? ' on' : '')}
          aria-expanded={isOpen}
          onClick={() => onOpenChange(!isOpen)}
        >
          <span className="funnel" aria-hidden="true">☰</span>
          Filters
          {activeCount ? <span className="fbadge">{activeCount}</span> : null}
        </button>

        {isOpen ? (
          <div className="filterpanel" role="group" aria-label="Filter accounts" ref={panelRef}
            style={placement
              ? { top: placement.top, left: placement.left, maxHeight: placement.maxHeight }
              : { visibility: 'hidden' }}>
            {children}
            <div className="ffoot">
              <button
                type="button"
                className="fclear"
                onClick={() => {
                  onClearAll();
                  onOpenChange(false);
                  buttonRef.current?.focus();
                }}
              >
                Clear all
              </button>
              <span className="fnote">{note}</span>
            </div>
          </div>
        ) : null}
      </div>

      {/* Whatever is active, always on the page. A collapsed panel is only safe because a
          filter you forgot you set is named here rather than looking like missing data. */}
      <div className="filterchips">{chips}</div>
    </>
  );
}
